import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Ban, CheckCircle2, Compass, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/pages/client/Dashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import GeoMap from "@/components/GeoMap";

export default function AdminGeofences() {
  const [geofences, setGeofences] = useState(null);
  const [mapFocus, setMapFocus] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/admin/geofences").then((r) => setGeofences(r.data)).catch(() => setGeofences([]));
  useEffect(() => { load(); }, []);

  const toggleStatus = async (g) => {
    const next = g.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
    try {
      await api.put(`/admin/geofences/${g.id}`, { ...g, status: next });
      toast.success(`${g.location_name} ${next === "ACTIVE" ? "activated" : "disabled"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete geofence "${g.location_name}"?`)) return;
    try {
      await api.delete(`/admin/geofences/${g.id}`);
      toast.success("Geofence deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!geofences) return <Skeleton className="h-64 bg-white/5" />;

  return (
    <div className="space-y-6" data-testid="admin-geofences-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="section-label">Geofence Management</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">Geofences</h2>
        </div>
        <Button onClick={() => navigate("/admin/geofence-builder")} className="bg-blue-600 hover:bg-blue-500" data-testid="open-builder-btn">
          <Compass className="w-4 h-4 mr-2" /> Open Geofence Builder
        </Button>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="geofences-table">
            <thead>
              <tr className="text-left text-[10px] tracking-widest text-slate-500 uppercase border-b border-white/5 bg-secondary/20">
                <th className="px-5 py-3.5">Location</th><th className="px-5 py-3.5">Coordinates</th>
                <th className="px-5 py-3.5">Radius</th><th className="px-5 py-3.5">Client</th>
                <th className="px-5 py-3.5">Campaign</th><th className="px-5 py-3.5">Active Dates</th>
                <th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {geofences.map((g) => (
                <tr key={g.id} className="text-slate-300 hover:bg-white/[0.02]" data-testid={`geofence-row-${g.id}`}>
                  <td className="px-5 py-3.5 font-semibold text-white">{g.location_name}<p className="text-[10px] text-slate-500 font-normal">{g.address}</p></td>
                  <td className="px-5 py-3.5 font-mono text-xs">{g.latitude.toFixed(4)}, {g.longitude.toFixed(4)}</td>
                  <td className="px-5 py-3.5">{g.radius_meters}m</td>
                  <td className="px-5 py-3.5 text-xs">{g.client_name}</td>
                  <td className="px-5 py-3.5 text-xs">{g.campaign_name}</td>
                  <td className="px-5 py-3.5 text-xs font-mono">{g.start_date} → {g.end_date}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={g.status} /></td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="h-8 text-cyan-400" onClick={() => setMapFocus(g)} data-testid={`view-geofence-${g.id}`}><MapPin className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 text-amber-400" onClick={() => toggleStatus(g)} data-testid={`toggle-geofence-${g.id}`}>
                      {g.status === "ACTIVE" ? <Ban className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-red-400" onClick={() => remove(g)} data-testid={`delete-geofence-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!mapFocus} onOpenChange={() => setMapFocus(null)}>
        <DialogContent className="bg-popover border-white/10 max-w-3xl" data-testid="admin-geofence-map-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{mapFocus?.location_name}</DialogTitle>
          </DialogHeader>
          {mapFocus && <GeoMap geofences={[mapFocus]} height="420px" testid="admin-geofence-map" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
